import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { CartoucheLivraisonItem, CartouchesService } from '../../../services/cartouches.service';

@Component({
  selector: 'app-cartouches-livrees',
  imports: [CommonModule],
  templateUrl: './cartouches-livrees.component.html',
  styleUrl: './cartouches-livrees.component.scss'
})
export class CartouchesLivreesComponent implements OnInit {
  protected loading = false;
  protected error = '';
  protected items: CartoucheLivraisonItem[] = [];
  protected filterValue = '';
  protected pageSize = 10;
  protected currentPage = 1;
  protected readonly pageSizeOptions = [10, 25, 50, 100];

  constructor(
    private readonly cartouchesService: CartouchesService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.load();
  }

  protected refresh(): void {
    this.load();
  }

  protected onFilterChange(value: string): void {
    this.filterValue = value;
    this.currentPage = 1;
    this.cdr.markForCheck();
  }

  protected onPageSizeChange(value: string): void {
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      return;
    }

    this.pageSize = parsed;
    this.currentPage = 1;
    this.cdr.markForCheck();
  }

  protected goToPreviousPage(): void {
    if (this.currentPage > 1) {
      this.currentPage -= 1;
      this.cdr.markForCheck();
    }
  }

  protected goToNextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage += 1;
      this.cdr.markForCheck();
    }
  }

  protected get filteredItems(): CartoucheLivraisonItem[] {
    const needle = this.filterValue.trim().toLowerCase();
    if (!needle) {
      return this.items;
    }

    return this.items.filter((item) => {
      const idText = String(item.id);
      const nomText = String(item.nom ?? '').toLowerCase();
      return idText.includes(needle) || nomText.includes(needle);
    });
  }

  protected get totalPages(): number {
    return Math.max(1, Math.ceil(this.filteredItems.length / this.pageSize));
  }

  protected get pagedItems(): CartoucheLivraisonItem[] {
    const maxPage = this.totalPages;
    const safePage = Math.min(Math.max(this.currentPage, 1), maxPage);
    if (safePage !== this.currentPage) {
      this.currentPage = safePage;
    }

    const start = (safePage - 1) * this.pageSize;
    const end = start + this.pageSize;
    return this.filteredItems.slice(start, end);
  }

  protected get pageStart(): number {
    if (this.filteredItems.length === 0) {
      return 0;
    }

    return (this.currentPage - 1) * this.pageSize + 1;
  }

  protected get pageEnd(): number {
    return Math.min(this.currentPage * this.pageSize, this.filteredItems.length);
  }

  private load(): void {
    this.loading = true;
    this.error = '';
    this.cdr.markForCheck();

    this.cartouchesService.listLivrees().subscribe({
      next: (items: CartoucheLivraisonItem[]) => {
        this.items = items;
        this.currentPage = 1;
        this.loading = false;
        this.cdr.markForCheck();
      },
      error: (err: HttpErrorResponse) => {
        this.error = this.getErrorMessage(err);
        this.items = [];
        this.currentPage = 1;
        this.loading = false;
        this.cdr.markForCheck();
      }
    });
  }

  private getErrorMessage(err: HttpErrorResponse): string {
    if (typeof err.error === 'string' && err.error.trim()) {
      return err.error;
    }
    if (err.error?.detail) {
      return String(err.error.detail);
    }
    if (err.error?.message) {
      return String(err.error.message);
    }
    return err.statusText || 'Erreur inconnue';
  }
}
